import { z } from 'zod';
import { apiUrl } from '../config/api';
import type { Employee } from '../data/databaseSchema';
import { apiClient } from '../lib/apiClient';
import { ApiError } from '../lib/errors';
import {
  employeeLocationModelsDetailSchema,
  employeeSchema,
  employeeSimpleSchema,
  employeeWorkingModelSchema,
  employeeWorkingModelsDetailSchema,
  envelopeSchema,
} from '../lib/schemas';

export interface EmployeeSimple {
  employee_id: string;
  name: string;
}

export const employeeService = {
  async getEmployees(): Promise<Employee[]> {
    const result = await apiClient.get(apiUrl.employees(), {
      validate: envelopeSchema('employees', z.array(employeeSchema)),
      deduplicate: true,
      context: 'getEmployees',
    });
    return result.employees;
  },

  async getActiveEmployeesSimple(): Promise<EmployeeSimple[]> {
    const result = await apiClient.get(apiUrl.activeEmployeesSimple(), {
      validate: envelopeSchema('employees', z.array(employeeSimpleSchema)),
      deduplicate: true,
      context: 'getActiveEmployeesSimple',
    });
    return result.employees;
  },

  async createEmployee(employee: Partial<Employee>): Promise<Employee> {
    const result = await apiClient.post(apiUrl.employees(), employee, {
      validate: envelopeSchema('employee', employeeSchema),
      context: 'createEmployee',
    });
    return result.employee;
  },

  async updateEmployee(id: string, employee: Partial<Employee>): Promise<Employee> {
    const result = await apiClient.put(apiUrl.employees(id), employee, {
      validate: envelopeSchema('employee', employeeSchema),
      context: 'updateEmployee',
    });
    return result.employee;
  },

  async deleteEmployee(id: string): Promise<void> {
    await apiClient.delete(apiUrl.employees(id), {
      context: 'deleteEmployee',
    });
  },

  async getEmployeeTimeModel(employeeId: string): Promise<EmployeeWorkingModelsDetail[]> {
    try {
      const result = await apiClient.get(apiUrl.employeeTimeModel(employeeId), {
        validate: envelopeSchema('time_models', z.array(employeeWorkingModelsDetailSchema)),
        deduplicate: true,
        context: 'getEmployeeTimeModel',
      });
      return result.time_models;
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return [];
      }
      throw error;
    }
  },

  async createEmployeeWorkingModel(
    employeeId: string,
    data: {
      working_time_model_id: string;
      start_date: string;
      end_date?: string;
      no_end_date?: boolean;
      status?: string;
    }
  ): Promise<EmployeeWorkingModelsDetail> {
    const result = await apiClient.post(`${apiUrl.employeeTimeModel(employeeId)}`, data, {
      validate: envelopeSchema('time_model', employeeWorkingModelSchema),
      context: 'createEmployeeWorkingModel',
    });
    return shapeWorkingModel(result.time_model);
  },

  async updateEmployeeWorkingModel(
    employeeId: string,
    id: string | number,
    data: {
      working_time_model_id: string;
      start_date: string;
      end_date?: string;
      no_end_date?: boolean;
      status?: string;
    }
  ): Promise<EmployeeWorkingModelsDetail> {
    const result = await apiClient.put(`${apiUrl.employeeTimeModel(employeeId)}/${id}`, data, {
      validate: envelopeSchema('time_model', employeeWorkingModelSchema),
      context: 'updateEmployeeWorkingModel',
    });
    return shapeWorkingModel(result.time_model);
  },

  async deleteEmployeeWorkingModel(employeeId: string, id: string | number): Promise<void> {
    await apiClient.delete(`${apiUrl.employeeTimeModel(employeeId)}/${id}`, {
      context: 'deleteEmployeeWorkingModel',
    });
  },

  async getEmployeeLocationModel(employeeId: string): Promise<EmployeeLocationModelsDetail[]> {
    const result = await apiClient.get(apiUrl.employeeLocationModel(employeeId), {
      validate: envelopeSchema(
        'employee_location_models',
        z.array(employeeLocationModelsDetailSchema)
      ),
      deduplicate: true,
      context: 'getEmployeeLocationModel',
    });
    return result.employee_location_models;
  },

  async createEmployeeLocationModel(
    employeeId: string,
    data: {
      location_id: string;
      start_date: string;
      end_date?: string;
      no_end_date?: boolean;
      status?: string;
    }
  ): Promise<EmployeeLocationModelsDetail> {
    const result = await apiClient.post(`${apiUrl.employeeLocationModel(employeeId)}`, data, {
      validate: envelopeSchema('employee_location_model', employeeLocationModelsDetailSchema),
      context: 'createEmployeeLocationModel',
    });
    return result.employee_location_model;
  },

  async updateEmployeeLocationModel(
    employeeId: string,
    id: string | number,
    data: {
      location_id: string;
      start_date: string;
      end_date?: string;
      no_end_date?: boolean;
      status?: string;
    }
  ): Promise<EmployeeLocationModelsDetail> {
    const result = await apiClient.put(`${apiUrl.employeeLocationModel(employeeId)}/${id}`, data, {
      validate: envelopeSchema('employee_location_model', employeeLocationModelsDetailSchema),
      context: 'updateEmployeeLocationModel',
    });
    return result.employee_location_model;
  },

  async deleteEmployeeLocationModel(employeeId: string, id: string | number): Promise<void> {
    await apiClient.delete(`${apiUrl.employeeLocationModel(employeeId)}/${id}`, {
      context: 'deleteEmployeeLocationModel',
    });
  },
};

// shapeWorkingModel projects a backend EmployeeWorkingModel into the detail shape
// the UI expects (a flattened view with the day breakdown promoted to top-level).
function shapeWorkingModel(
  model: z.infer<typeof employeeWorkingModelSchema>
): EmployeeWorkingModelsDetail {
  return {
    id: model.id,
    working_time_model_id: model.working_time_model_id,
    name: model.working_time_model?.name || '',
    hours: model.working_time_model?.hours || 0,
    working_time_model: {
      monday: model.working_time_model?.monday || 0,
      tuesday: model.working_time_model?.tuesday || 0,
      wednesday: model.working_time_model?.wednesday || 0,
      thursday: model.working_time_model?.thursday || 0,
      friday: model.working_time_model?.friday || 0,
      saturday: model.working_time_model?.saturday || 0,
      sunday: model.working_time_model?.sunday || 0,
    },
    start_date: model.start_date,
    end_date: model.end_date,
    no_end_date: model.no_end_date,
    status: model.status || 'active',
  };
}

export interface EmployeeWorkingModelsDetail {
  id: string;
  working_time_model_id: string;
  name: string;
  hours: number;
  working_time_model: WorkingTimeDaysModel;
  start_date: string;
  end_date: string;
  no_end_date?: boolean;
  status: string;
}

interface WorkingTimeDaysModel {
  monday: number;
  tuesday: number;
  wednesday: number;
  thursday: number;
  friday: number;
  saturday: number;
  sunday: number;
}

export interface EmployeeLocationModelsDetail {
  id: string;
  location_id: string;
  location: {
    id: string;
    name: string;
    office_head: string;
  };
  start_date: string;
  end_date: string;
  no_end_date?: boolean;
  status: string;
}
